import React, { useContext, useEffect, useMemo, useState } from "react";
import { baseUrl } from "../../common/constants";
import _axios from "../../utils/api/_axios";
import { useNavigate } from "react-router-dom";
import { mainContext } from "../../context/context";
import { Table, Spin } from "antd";
import { ReloadOutlined, DownloadOutlined } from "@ant-design/icons";

const NewEntries = () => {
  const navigate = useNavigate();
  const [newEntries, setNewEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const { getFormData } = useContext(mainContext);
  const [activeTab, setActiveTab] = useState(0);
  const tabs = ["All Entries", "New Entries", "Rejected"];
  const tabCounts = useMemo(() => {
    const all = newEntries.length;
    const newOnly = newEntries.filter(
      (entry) => !entry.isAdminApproved && !entry.isRejected
    ).length;
    const rejected = newEntries.filter((entry) => entry.isRejected).length;
    return [all, newOnly, rejected];
  }, [newEntries]);
  
  const filteredEntries = useMemo(() => {
    if (activeTab === 1)
      return newEntries.filter(
        (entry) => !entry.isAdminApproved && !entry.isRejected
      );
    if (activeTab === 2) return newEntries.filter((entry) => entry.isRejected);
    return newEntries;
  }, [activeTab, newEntries]);

  const getNewEntries = async () => {
    setLoading(true);
    try {
      const res = await _axios.get(`${baseUrl}/openAccess`, {
        headers: { "Content-Type": "application/json" },
      });
      const response = res.data;
      const newEntriesData = response?.payload?.message
        ? []
        : response?.payload;
      setNewEntries(newEntriesData);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getNewEntries();
  }, []);

  const handleReject = async (id) => {
    try {
      await _axios.post(`${baseUrl}/openAccess/reject/${id}`, {
        headers: { "Content-Type": "application/json" },
      });
  
      // Optimistically update the rejected status in the current list
      setNewEntries((prevEntries) =>
        prevEntries.map((entry) =>
          entry._id === id ? { ...entry, isRejected: true } : entry
        )
      );
    } catch (err) {
      console.error(err);
    }
  };
  

  const handleView = (id, record) => {
    const formData = {
      patientInfo: record.patientInfo,
      employeeInfo: record.employeeInfo,
      referredByInfo: {
        ...record.referredByInfo,
        insuranceCompanies: record.referredByInfo.insuranceCompanies || [],
      },
      proceduresInfo: record.proceduresInfo,
      specialInstruction: record.specialInstruction,
    };
    getFormData(formData);
    navigate(`/new-entry/details/${id}`);
  };

  return (
    <div className="bg-gray-100 min-h-screen p-6">
      <div className="max-w-7xl mx-auto space-y-4">
        {/* Navbar */}
        <div className="flex flex-col bg-white shadow rounded-lg px-6 py-4 space-y-4">
  {/* Title */}
  <div className="flex items-center justify-between">
    <h2 className="text-2xl font-semibold text-gray-800">Claims Verification</h2>

  </div>

  {/* Tabs */}
  <div className="flex space-x-6 border-b border-gray-200 text-sm font-medium">
    {["All Entries", "New Entries", "Rejected"].map((tab, idx) => (
      <button
        key={tab}
        onClick={() => setActiveTab(idx)}
        className={`pb-2 relative transition-colors duration-200 ${
          activeTab === idx
            ? "text-blue-600"
            : "text-gray-500 hover:text-blue-600"
        }`}
      >
        {tab}
        <span className="ml-1 text-xs text-gray-400">({tabCounts[idx]})</span>
        {activeTab === idx && (
          <span className="absolute bottom-0 left-0 w-full h-0.5 bg-blue-600 rounded-t"></span>
        )}
      </button>
    ))}
  </div>
</div>


        {/* Tabs */}

        {/* Main Content Box */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <header className="mb-6">
            <h1 className="text-2xl font-semibold">{tabs[activeTab]}</h1>
          </header>

          {/* Table Section */}
          {loading ? (
            <div className="text-center py-10">
              <Spin size="large" />
            </div>
          ) : filteredEntries.length === 0 ? (
            <div className="text-center text-lg mt-8">No data available</div>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
              <table className="w-full table-auto text-sm text-left">
                <thead className="bg-gray-50 text-gray-700">
                  <tr>
                    <th className="px-4 py-3">S.No</th>
                    <th className="px-4 py-3">First Name</th>
                    <th className="px-4 py-3">Last Name</th>
                    <th className="px-4 py-3">Email</th>
                    <th className="px-4 py-3">Phone</th>
                    <th className="px-4 py-3">Claim Number</th>
                    <th className="px-4 py-3">Insurance Company</th>
                    <th className="px-4 py-3">Adjuster</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEntries.map((entry, index) => (
                    <tr key={entry._id} className="hover:bg-gray-50 border-t">
                      <td className="px-4 py-2">{index + 1}</td>
                      <td className="px-4 py-2">
                        {entry?.patientInfo?.firstName}
                      </td>
                      <td className="px-4 py-2">
                        {entry?.patientInfo?.lastName}
                      </td>
                      <td className="px-4 py-2">{entry?.patientInfo?.email}</td>
                      <td className="px-4 py-2">
                        {entry?.patientInfo?.mobile}
                      </td>
                      <td className="px-4 py-2">{entry?.claimNumber || "-"}</td>
                      <td className="px-4 py-2">
                        {entry?.insuranceCompany || "-"}
                      </td>
                      <td className="px-4 py-2">{entry?.adjuster || "-"}</td>
                      <td className="px-4 py-2">
                        {entry?.isRejected ? (
                          <span className="inline-block bg-red-100 text-red-700 text-xs px-2 py-1 rounded-full font-medium">
                            Rejected
                          </span>
                        ) : entry?.isAdminApproved ? (
                          <span className="inline-block bg-green-100 text-green-700 text-xs px-2 py-1 rounded-full font-medium">
                            Verified
                          </span>
                        ) : (
                          <span className="inline-block bg-yellow-100 text-yellow-700 text-xs px-2 py-1 rounded-full font-medium">
                            Pending
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2">
                        <div className="flex items-center gap-2">
                          <button
                            className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded"
                            onClick={() => handleView(entry._id, entry)}
                          >
                            View
                          </button>
                          <button
                            className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded"
                            onClick={() => handleReject(entry._id)}
                          >
                            Reject
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Footer */}
          {/* <footer className="flex justify-between items-center mt-6 text-sm text-gray-600">
            <p>
              Showing 1 to {filteredEntries.length} of {filteredEntries.length}{" "}
              entries
            </p>
            <div className="space-x-2">
              <button className="bg-blue-600 text-white px-3 py-1 rounded">
                Previous
              </button>
              <button className="bg-blue-600 text-white px-3 py-1 rounded">
                Next
              </button>
            </div>
          </footer> */}
        </div>
      </div>
    </div>
  );
};

export default NewEntries;
